import { Controller } from "@hotwired/stimulus"

// Generic stack-coordinator. Used wherever multiple "stacks of cards"
// share a row and only one should be fanned out at a time — the skills
// section, the model-show gallery, etc.
//
// Each stack is in state "stacked" (default) or "fanned". When stacked,
// a dedicated overlay <button> sits absolutely on top of the cards and
// captures all clicks → fires #toggle → flips state to "fanned". When
// the stack is fanned, the overlay is hidden via CSS so the card
// buttons underneath receive clicks normally and open the canonical
// lightbox via their own click->lightbox#open binding.
//
// Only one stack is fanned at a time. Clicking a different stack while
// one is open closes the current one first (~360ms), then opens the
// new one — the user sees a clean re-stack → unstack handoff.
export default class extends Controller<HTMLElement> {
  static targets = ["stack"]

  declare readonly stackTargets: HTMLElement[]

  private openIndex: number | null = null
  private outsideHandler: ((event: MouseEvent) => void) | null = null

  disconnect() {
    this.detachOutsideHandler()
  }

  toggle(event: Event) {
    const trigger = event.currentTarget as HTMLElement
    const stack = trigger.closest("[data-stack-group-target=\"stack\"]") as HTMLElement | null
    if (!stack) return
    const idx = this.stackTargets.indexOf(stack)
    if (idx < 0) return

    // Same stack tapped while open → close.
    if (this.openIndex === idx) {
      this.closeOpen()
      return
    }

    // Different stack tapped: close current, then open new after the
    // CSS transition wraps up (~220ms). Matches the duration on
    // .comp-stack__card.
    if (this.openIndex !== null && this.stackTargets[this.openIndex]) {
      this.closeOpen()
      window.setTimeout(() => this.open(stack, idx), 220)
    } else {
      this.open(stack, idx)
    }
  }

  private open(stack: HTMLElement, idx: number) {
    this.setState(stack, "fanned")
    this.openIndex = idx
    this.attachOutsideHandler()
  }

  private closeOpen() {
    if (this.openIndex !== null && this.stackTargets[this.openIndex]) {
      this.setState(this.stackTargets[this.openIndex], "stacked")
    }
    this.openIndex = null
    this.detachOutsideHandler()
  }

  // Document-level click listener — closes the open stack when the user
  // clicks anywhere that isn't inside it. Attached only while a stack
  // is open and removed on close so we don't add a permanent listener.
  // We listen in the capture phase so clicks on lightbox triggers still
  // open the lightbox normally (the close runs before their handler).
  private attachOutsideHandler() {
    if (this.outsideHandler) return
    this.outsideHandler = (event: MouseEvent) => {
      const target = event.target as (Node & Element) | null
      if (this.openIndex === null) return
      const openStack = this.stackTargets[this.openIndex]
      if (openStack && target && openStack.contains(target)) return
      // Clicks that land on an open lightbox (e.g. the modal backdrop
      // that closes the lightbox) shouldn't propagate to close the
      // underlying fanned stack — the lightbox was opened from a card
      // in that stack, and the user expects to land back on the fan.
      if (target && "closest" in target && target.closest("dialog[open]")) return
      this.closeOpen()
    }
    // Defer attach to next tick so the click that opened the stack
    // doesn't immediately bubble up and close it.
    window.setTimeout(() => {
      if (this.outsideHandler) {
        document.addEventListener("click", this.outsideHandler, true)
      }
    }, 0)
  }

  private detachOutsideHandler() {
    if (!this.outsideHandler) return
    document.removeEventListener("click", this.outsideHandler, true)
    this.outsideHandler = null
  }

  private setState(stack: HTMLElement, state: "stacked" | "fanned") {
    stack.dataset.state = state
  }
}
